import { VFC } from 'react';

import { MemberSearchButton } from '../../MemberSearchButton';
import { HospitalSearchButton, HospitalConverter } from '../../HospitalSearchButton';
import { getRegion } from '../../../../hooks/useRegion';
import { components } from '../../../../schema/hospital-management';

type InputTextAreaProps = {
  value: string;
  onNewValueSet: (newValue: string) => void;
  fieldId: string;
  disabled?: boolean;
};

export const InputTextArea: VFC<InputTextAreaProps> = ({
  value,
  onNewValueSet,
  fieldId,
  disabled,
}) => {
  const resizeHeight = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    e.target.style.height = 'auto';
    e.target.style.height = e.target.scrollHeight + 2 + 'px';
  };
  const fieldIdForMemberSearch = getMemberSearch(fieldId);
  const hospitalConverter = getHospitalConverter(fieldId);

  return (
    <div className="py-1 px-2 flex items-center">
      <textarea
        className={`font-bold ${
          disabled ? 'text-gray-500' : 'text-gray-900'
        } w-full px-3 py-2 text-left rounded bg-transparent border-transparent`}
        value={value}
        onChange={(e) => {
          onNewValueSet(e.target.value);
          resizeHeight(e);
        }}
        disabled={disabled}
      />
      {fieldIdForMemberSearch && (
        <MemberSearchButton fieldIdForAutoComplete={fieldIdForMemberSearch} />
      )}
      {hospitalConverter && <HospitalSearchButton convertHospitalToFields={hospitalConverter} />}
    </div>
  );
};

const getMemberSearch = (fieldId: string) => {
  const region = getRegion();
  if (region === 'Default') {
    switch (fieldId) {
      // 出場情報 出動概要 報告者
      case '0e6a4fcd-2637-478f-a7c5-9614b0596a2e':
        return {
          memberCode: '0e6a4fcd-2637-478f-a7c5-9614b0596a2e', // 報告者コード
          memberName: '4efff949-e018-47c1-8220-f73ce16b08c6', // 報告者氏名
        };

      default:
        return undefined;
    }
  }
  if (region === 'Fujisawa') {
    switch (fieldId) {
      // 出場情報 出動概要 報告者
      case '0e6a4fcd-2637-478f-a7c5-9614b0596a2e':
        return {
          memberCode: '0e6a4fcd-2637-478f-a7c5-9614b0596a2e', // 報告者コード
          memberName: '4efff949-e018-47c1-8220-f73ce16b08c6', // 報告者氏名
        };

      // 出場情報 隊情報 隊長
      case '1f5026fc-6b38-4194-a68f-11f7607e2557':
        return {
          memberCode: '1f5026fc-6b38-4194-a68f-11f7607e2557', // 隊長コード
          memberName: '460d8e80-29d7-4326-a6c8-087476bdc68f', // 隊長氏名
          licenseStatus: 'bec731fc-138a-4bb5-82f3-b8222c5c5a2c', // 隊長資格認定状況
          emtClass: '5a0e88fb-3bef-4a6c-8f84-f48d0565b301', // 隊長救急救命士認定区分
        };

      // 出場情報 隊情報 隊員1
      case '00f6eb54-7e5a-4b73-92d4-c1efff166ce9':
        return {
          memberCode: '00f6eb54-7e5a-4b73-92d4-c1efff166ce9', // 隊員1コード
          memberName: '61b1265c-fefd-4283-bb8a-d6d17f5b9597', // 隊員1氏名
          licenseStatus: '52fa44e7-840b-417a-bb69-853678b16183', // 隊員1資格認定状況
          emtClass: 'fb82782d-94bd-4ebb-9e0d-f9bece19209b', // 隊員1救急救命士認定区分
        };

      // 出場情報 隊情報 隊員2
      case 'b807be4a-3b66-44c3-8b42-ae6b7d8d819f':
        return {
          memberCode: 'b807be4a-3b66-44c3-8b42-ae6b7d8d819f', // 隊員2コード
          memberName: 'eb3d9b9e-5bf6-40f0-b32a-5a5a20581c5e', // 隊員2氏名
          licenseStatus: 'da89de6e-f029-48b3-ada2-e760b1b379ea', // 隊員2資格認定状況
          emtClass: '8af9f5b6-48f2-4c07-8216-e1731d96fd50', // 隊員2救急救命士認定区分
        };

      // 出場情報 隊情報 機関員
      case 'aba49058-ed75-429b-9757-a5af3a715448':
        return {
          memberCode: 'aba49058-ed75-429b-9757-a5af3a715448', // 機関員コード
          memberName: '80d0f4c6-5a5f-4b38-85a2-bfc992c4877f', // 機関員氏名
          licenseStatus: 'f96855c7-14e5-4945-ab45-d7a125b123ca', // 機関員資格認定状況
          emtClass: 'c079cd66-8e4a-4d23-892d-4818c36337a0', // 機関員救急救命士認定区分
        };

      // 傷病者情報 市民処置 口頭指導員
      case 'd5ef9ed8-8c73-4ddc-bb57-c35eefabd819':
        return {
          code: 'd5ef9ed8-8c73-4ddc-bb57-c35eefabd819', // 口頭指導員コード
          memberName: '870df4f6-3dda-4ffa-b8ba-153fe4e98ab2', // 口頭指導員氏名
        };

      // 傷病者情報 特定行為 除細動1実施者
      case 'aa6e1e6a-39b1-48a2-b55b-5ab8ccbe6985':
        return {
          memberName: 'aa6e1e6a-39b1-48a2-b55b-5ab8ccbe6985', // 除細動1実施者氏名
        };

      // 傷病者情報 特定行為 除細動2実施者
      case '8750a904-aa0c-4a66-886c-16a67b3e9dab':
        return {
          memberName: '8750a904-aa0c-4a66-886c-16a67b3e9dab', // 除細動2実施者氏名
        };

      // 傷病者情報 特定行為 除細動3実施者
      case '8d8c6330-2cad-40ac-baa1-8261cb75c587':
        return {
          memberName: '8d8c6330-2cad-40ac-baa1-8261cb75c587', // 除細動3実施者氏名
        };

      // 傷病者情報 特定行為 除細動4実施者
      case '8bcd2fbd-b560-4d89-8891-82c6fbe75329':
        return {
          memberName: '8bcd2fbd-b560-4d89-8891-82c6fbe75329', // 除細動4実施者氏名
        };

      // 傷病者情報 特定行為 除細動5実施者
      case 'aec0c456-91eb-44c6-81cf-e6f568c44620':
        return {
          memberName: 'aec0c456-91eb-44c6-81cf-e6f568c44620', // 除細動5実施者氏名
        };

      // 傷病者情報 特定行為 除細動6実施者
      case 'd59fd9ef-c31e-446f-a93a-d84d4bfeab24':
        return {
          memberName: 'd59fd9ef-c31e-446f-a93a-d84d4bfeab24', // 除細動6実施者氏名
        };

      // 傷病書情報 特定行為 気道確保実施者
      case 'd42b554a-2c83-419d-a703-8d2b67af6516':
        return {
          memberCode: 'd42b554a-2c83-419d-a703-8d2b67af6516', // 気道確保実施者コード
          memberName: '38968319-0608-4be6-a636-faddbd36e7c6', // 気道確保実施者氏名
        };

      // 傷病書情報 特定行為 薬剤投与1実施者
      case '785ee942-005d-4a8c-9e55-54bdca2a6b33':
        return {
          memberCode: '785ee942-005d-4a8c-9e55-54bdca2a6b33', // 薬剤投与1実施者コード
          memberName: 'ef33e53c-f125-4292-ac71-759281e6063c', // 薬剤投与1実施者氏名
        };

      // 傷病書情報 特定行為 薬剤投与2実施者
      case 'ac3134f4-e1c5-4cc7-b4a9-d98c7e218db7':
        return {
          memberCode: 'ac3134f4-e1c5-4cc7-b4a9-d98c7e218db7', // 薬剤投与2実施者コード
          memberName: '1331fea9-f1d3-4d7b-9f48-7a98d6d64302', // 薬剤投与2実施者氏名
        };

      // 傷病書情報 特定行為 薬剤投与3実施者
      case 'cf2c63f6-7c28-41bd-86b5-d41b3f11d91d':
        return {
          memberCode: 'cf2c63f6-7c28-41bd-86b5-d41b3f11d91d', // 薬剤投与3実施者コード
          memberName: 'cbb466b7-17e2-4607-b4ff-66b5db1abea1', // 薬剤投与3実施者氏名
        };

      // 傷病書情報 特定行為 薬剤投与4実施者
      case 'a3d58451-717c-403a-8efc-a47eb74d0c24':
        return {
          memberCode: 'a3d58451-717c-403a-8efc-a47eb74d0c24', // 薬剤投与4実施者コード
          memberName: '5d7f9378-effe-4393-bdd1-5475eb8a2227', // 薬剤投与4実施者氏名
        };

      // 傷病書情報 特定行為 薬剤投与5実施者
      case 'aeee20c2-ab53-42f0-85d1-4edde965172b':
        return {
          memberCode: 'aeee20c2-ab53-42f0-85d1-4edde965172b', // 薬剤投与5実施者コード
          memberName: '69bf7078-621a-4fe2-a99d-62e7b30b1dc4', // 薬剤投与5実施者氏名
        };

      default:
        return undefined;
    }
  }
};

const getHospitalConverter = (fieldId: string): HospitalConverter | undefined => {
  const region = getRegion();
  if (region === 'Default') {
    switch (fieldId) {
      // ------------------------------
      // 出場情報 出動概要 要請機関
      // ------------------------------
      // 要請機関：コードに検索ボタンを設置
      case '39c430d5-d1e2-4b0d-b012-af21aaddd67e':
        return (hospital) => [
          // 要請機関：コード
          {
            fieldId: '39c430d5-d1e2-4b0d-b012-af21aaddd67e',
            value: hospital.conveyorCode,
          },
          // 要請機関：形態
          {
            fieldId: 'edffd65e-e6e4-4641-a49b-aaaeaf2e1291',
            value: stringifyEmbodimentType(hospital.hospitalEmbodimentType),
          },
          // 要請機関：告示
          {
            fieldId: 'afb002b4-c8ac-4eb1-b06c-9490ec6de615',
            value: stringifyEmergencyType(hospital.hospitalEmergencyType),
          },
          // 要請機関：設立
          {
            fieldId: '97895b95-2bf2-4c64-8edd-0d53a66d8a7a',
            value: stringifyEstablishmentType(hospital.hospitalEstablishmentType),
          },
          // 要請機関名
          {
            fieldId: 'c184fef9-5a08-4cdf-aecb-3963831ec923',
            value: hospital.hospitalName,
          },
        ];
      // 要請機関名に検索ボタンを設置
      case 'c184fef9-5a08-4cdf-aecb-3963831ec923':
        return (hospital) => [
          // 要請機関：コード
          {
            fieldId: '39c430d5-d1e2-4b0d-b012-af21aaddd67e',
            value: hospital.conveyorCode,
          },
          // 要請機関：形態
          {
            fieldId: 'edffd65e-e6e4-4641-a49b-aaaeaf2e1291',
            value: stringifyEmbodimentType(hospital.hospitalEmbodimentType),
          },
          // 要請機関：告示
          {
            fieldId: 'afb002b4-c8ac-4eb1-b06c-9490ec6de615',
            value: stringifyEmergencyType(hospital.hospitalEmergencyType),
          },
          // 要請機関：設立
          {
            fieldId: '97895b95-2bf2-4c64-8edd-0d53a66d8a7a',
            value: stringifyEstablishmentType(hospital.hospitalEstablishmentType),
          },
          // 要請機関名
          {
            fieldId: 'c184fef9-5a08-4cdf-aecb-3963831ec923',
            value: hospital.hospitalName,
          },
        ];
      // ------------------------------
      // 傷病者情報 搬送機関 搬送機関
      // ------------------------------
      // 機関名に検索ボタンを設置
      case 'f18808bd-eb91-4302-94f4-65a9ba1a2089':
        return (hospital) => [
          // 機関名
          {
            fieldId: 'f18808bd-eb91-4302-94f4-65a9ba1a2089',
            value: hospital.hospitalName,
          },
          // 搬送機関コード
          {
            fieldId: '77a3fc92-8e0f-4f73-bd84-26be4a3e3d6d',
            value: hospital.conveyorCode,
          },
          // 設立別
          {
            fieldId: '87f0e740-a12d-4ed4-8d1f-2a90427cd602',
            value: stringifyEstablishmentTypeAtPatientInfo(hospital.hospitalEstablishmentType),
          },
          // 私的 (設立別で「004:私的」を選択すると子項目として表示)
          {
            fieldId: 'b83fe369-2578-4737-b51e-43bf949ab5d4',
            value: stringifyEstablishmentTypeAtPatientInfoOnPrivateField(
              hospital.hospitalEstablishmentType
            ),
          },
          // その他 ((設立別で「005:その他（医療機関以外）」を選択すると子項目として表示)
          {
            fieldId: '0d8ad14b-ff7f-4ff3-93cc-3a81a2b65648',
            value: stringifyEstablishmentTypeAtPatientInfoOnOtherField(
              hospital.hospitalEstablishmentType
            ),
          },
          // 告示別
          {
            fieldId: '47a57166-ea56-4da3-b3b7-d5059c1df6b8',
            value: stringifyEmergencyTypeAtPatientInfo(hospital.hospitalEmergencyType),
          },
          // 実施形態
          {
            fieldId: 'b2186a96-0631-483a-80ba-131d4c2da840',
            value: stringifyEmbodimentTypeAtPatientInfo(hospital.hospitalEmbodimentType),
          },
          // 菅内・外別
          {
            fieldId: 'b610c294-2cb9-4598-ac22-61375fa5baba',
            value: stringifyJurisdiction(hospital.jurisdiction),
          },
        ];
      // 搬送機関コードに検索ボタンを設置
      case '77a3fc92-8e0f-4f73-bd84-26be4a3e3d6d':
        return (hospital) => [
          // 機関名
          {
            fieldId: 'f18808bd-eb91-4302-94f4-65a9ba1a2089',
            value: hospital.hospitalName,
          },
          // 搬送機関コード
          {
            fieldId: '77a3fc92-8e0f-4f73-bd84-26be4a3e3d6d',
            value: hospital.conveyorCode,
          },
          // 設立別
          {
            fieldId: '87f0e740-a12d-4ed4-8d1f-2a90427cd602',
            value: stringifyEstablishmentTypeAtPatientInfo(hospital.hospitalEstablishmentType),
          },
          // 私的 (設立別で「004:私的」を選択すると子項目として表示)
          {
            fieldId: 'b83fe369-2578-4737-b51e-43bf949ab5d4',
            value: stringifyEstablishmentTypeAtPatientInfoOnPrivateField(
              hospital.hospitalEstablishmentType
            ),
          },
          // その他 ((設立別で「005:その他（医療機関以外）」を選択すると子項目として表示)
          {
            fieldId: '0d8ad14b-ff7f-4ff3-93cc-3a81a2b65648',
            value: stringifyEstablishmentTypeAtPatientInfoOnOtherField(
              hospital.hospitalEstablishmentType
            ),
          },
          // 告示別
          {
            fieldId: '47a57166-ea56-4da3-b3b7-d5059c1df6b8',
            value: stringifyEmergencyTypeAtPatientInfo(hospital.hospitalEmergencyType),
          },
          // 実施形態
          {
            fieldId: 'b2186a96-0631-483a-80ba-131d4c2da840',
            value: stringifyEmbodimentTypeAtPatientInfo(hospital.hospitalEmbodimentType),
          },
          // 菅内・外別
          {
            fieldId: 'b610c294-2cb9-4598-ac22-61375fa5baba',
            value: stringifyJurisdiction(hospital.jurisdiction),
          },
        ];
      // ------------------------------
      // 傷病者情報 転送 転送病院1~5医療機関名
      // ------------------------------
      // 機関名に検索ボタンを設置
      case 'eeeae236-6968-42cf-928f-cb06d56c138c':
      case '12ed3f3e-bfce-4987-9260-6a018406c842':
      case '8d5c9d2b-d419-4323-a247-16bce249af50':
      case '82efe943-6e3f-4008-a29d-de2b238c8532':
      case '61cd236d-f908-47a4-b617-bc601d34be50':
        return (hospital) => [
          // 機関名
          {
            fieldId: fieldId,
            value: hospital.hospitalName,
          },
        ];
      default:
        return undefined;
    }
  }
  if (region === 'Fujisawa') {
    switch (fieldId) {
      // 出場情報 出動概要 転院元医療機関 (事故種別で「11:転院搬送」を選択すると子項目として表示)
      case '9b30f5df-2c65-40be-821b-90952da0f293':
        return (hospital) => [
          // 転院元医療機関コード
          {
            fieldId: '9b30f5df-2c65-40be-821b-90952da0f293',
            value: hospital.ownCode, // 藤沢独自コード
          },
          // 転院元医療機関名
          {
            fieldId: '8d53578f-e543-4782-9bc8-dda864fa2791',
            value: hospital.hospitalName,
          },
          // 転院元医療機関実施形態
          {
            fieldId: 'cb24025a-a1bc-4084-90e3-dfa9be59b5a1',
            value: stringifyEmbodimentTypeInFujisawa(hospital.hospitalEmbodimentType),
          },
        ];

      // 傷病者情報 基本情報 収容機関
      case '20936d4a-5035-4b04-a4d6-09d91f1bb892':
        return (hospital) => [
          // 収容機関コード
          {
            fieldId: '20936d4a-5035-4b04-a4d6-09d91f1bb892',
            value: hospital.ownCode, // 藤沢独自コード
          },
          // 収容機関名
          {
            fieldId: 'c2650e48-e885-4ee2-9309-1eec08d9e9dc',
            value: hospital.hospitalName,
          },
          // 病院管内管外区分
          {
            fieldId: '599ac010-800d-4214-b7ca-825fa898258c',
            value: stringifyJurisdictionInFujisawa(hospital.jurisdiction),
          },
          // 収容機関実施形態(非表示項目)
          {
            fieldId: '59262b07-c27e-49a0-a52d-ef3d18cfda2d',
            value: stringifyEmbodimentTypeInFujisawa(hospital.hospitalEmbodimentType),
          },
        ];

      // 傷病者情報 病歴 通院病院
      case '0ef47e60-dc01-4248-b62e-68a56b522976':
        return (hospital) => [
          // 通院病院コード
          {
            fieldId: '0ef47e60-dc01-4248-b62e-68a56b522976',
            value: hospital.ownCode, // 藤沢独自コード
          },
          // 通院病院名
          {
            fieldId: 'cb87c59d-76e5-41c5-b282-51e44054fe76',
            value: hospital.hospitalName,
          },
          // 通院病院実施形態(非表示項目)
          {
            fieldId: 'a55ad441-48e6-444a-856f-8d0d8c86b083',
            value: stringifyEmbodimentTypeInFujisawa(hospital.hospitalEmbodimentType),
          },
        ];

      // 傷病者情報 転送 転送病院1
      case 'eeeae236-6968-42cf-928f-cb06d56c138c':
        return (hospital) => [
          // 転送病院1医療機関名
          {
            fieldId: 'eeeae236-6968-42cf-928f-cb06d56c138c',
            value: hospital.hospitalName,
          },
          // 転送病院1告示
          {
            fieldId: 'dbdc3fb3-fd0c-4e0b-b935-713b380d7116',
            value: stringifyEmergencyTypeInFujisawa(hospital.hospitalEmergencyType),
          },
          // 転送病院1実施形態
          {
            fieldId: '546b5e48-2c6d-408b-b845-071d7d32409b',
            value: stringifyEmbodimentTypeInFujisawa(hospital.hospitalEmbodimentType),
          },
        ];

      // 傷病者情報 転送 転送病院2
      case '12ed3f3e-bfce-4987-9260-6a018406c842':
        return (hospital) => [
          // 転送病院2医療機関名
          {
            fieldId: '12ed3f3e-bfce-4987-9260-6a018406c842',
            value: hospital.hospitalName,
          },
          // 転送病院2告示
          {
            fieldId: '5fe62839-ba92-4bed-805f-b6bdc93edb52',
            value: stringifyEmergencyTypeInFujisawa(hospital.hospitalEmergencyType),
          },
          // 転送病院2実施形態
          {
            fieldId: '574fc789-73d2-4ec8-a53e-be0c88f70f9d',
            value: stringifyEmbodimentTypeInFujisawa(hospital.hospitalEmbodimentType),
          },
        ];

      // 傷病者情報 転送 転送病院3
      case '8d5c9d2b-d419-4323-a247-16bce249af50':
        return (hospital) => [
          // 転送病院3医療機関名
          {
            fieldId: '8d5c9d2b-d419-4323-a247-16bce249af50',
            value: hospital.hospitalName,
          },
          // 転送病院3告示
          {
            fieldId: 'e6b96207-aca1-4c4d-81a7-6528666d95e7',
            value: stringifyEmergencyTypeInFujisawa(hospital.hospitalEmergencyType),
          },
          // 転送病院3実施形態
          {
            fieldId: '3fc5d7eb-2d70-4049-bd80-9e1853404e19',
            value: stringifyEmbodimentTypeInFujisawa(hospital.hospitalEmbodimentType),
          },
        ];

      // 傷病者情報 転送 転送病院4
      case '82efe943-6e3f-4008-a29d-de2b238c8532':
        return (hospital) => [
          // 転送病院4医療機関名
          {
            fieldId: '82efe943-6e3f-4008-a29d-de2b238c8532',
            value: hospital.hospitalName,
          },
          // 転送病院4告示
          {
            fieldId: '01538d81-24be-4a34-ab29-e03f7eff9614',
            value: stringifyEmergencyTypeInFujisawa(hospital.hospitalEmergencyType),
          },
          // 転送病院4実施形態
          {
            fieldId: '8213ac46-f57d-4d3a-95d4-452f5a9fa79a',
            value: stringifyEmbodimentTypeInFujisawa(hospital.hospitalEmbodimentType),
          },
        ];

      // 傷病者情報 転送 転送病院5
      case '61cd236d-f908-47a4-b617-bc601d34be50':
        return (hospital) => [
          // 転送病院5医療機関名
          {
            fieldId: '61cd236d-f908-47a4-b617-bc601d34be50',
            value: hospital.hospitalName,
          },
          // 転送病院5告示
          {
            fieldId: '4f15b82f-e38b-4d10-ae92-9a1dc65e0be1',
            value: stringifyEmergencyTypeInFujisawa(hospital.hospitalEmergencyType),
          },
          // 転送病院5実施形態
          {
            fieldId: 'cd1f167c-42b2-451a-899f-934c86429261',
            value: stringifyEmbodimentTypeInFujisawa(hospital.hospitalEmbodimentType),
          },
        ];

      // 傷病者情報 転送 転送病院6
      case '83a81842-b50c-40a1-a55f-2857d96e396e':
        return (hospital) => [
          // 転送病院6医療機関名
          {
            fieldId: '83a81842-b50c-40a1-a55f-2857d96e396e',
            value: hospital.hospitalName,
          },
          // 転送病院6告示
          {
            fieldId: '56e45cf2-a262-4241-88c9-c0f598d4971d',
            value: stringifyEmergencyTypeInFujisawa(hospital.hospitalEmergencyType),
          },
          // 転送病院6実施形態
          {
            fieldId: '20673396-a584-47cd-a78f-cde1bd6087f9',
            value: stringifyEmbodimentTypeInFujisawa(hospital.hospitalEmbodimentType),
          },
        ];

      // 傷病者情報 救急救命処置 指示医療機関
      case '6bb4dce5-724f-4ac6-a07e-80a1c219c945':
        // return {
        //   code: '6bb4dce5-724f-4ac6-a07e-80a1c219c945', // 指示医療機関コード
        //   hospitalName: '7a9ecbca-2cd7-4198-a5c2-577ff6dbae8f', // 指示医療機関名
        //   hospitalEmbodimentType: 'cb046f7b-dbef-4f38-8585-f8e50217a74a', // 指示医療機関実施形態
        // };
        return (hospital) => [
          // 指示医療機関コード
          {
            fieldId: '6bb4dce5-724f-4ac6-a07e-80a1c219c945',
            value: hospital.ownCode, // 藤沢独自コード
          },
          // 指示医療機関名
          {
            fieldId: '7a9ecbca-2cd7-4198-a5c2-577ff6dbae8f',
            value: hospital.hospitalName,
          },
          // 指示医療機関実施形態
          {
            fieldId: 'cb046f7b-dbef-4f38-8585-f8e50217a74a',
            value: stringifyEmbodimentTypeInFujisawa(hospital.hospitalEmbodimentType),
          },
        ];

      default:
        // 傷病者情報 病院問い合わせ
        if (fieldId in fujisawaHospitalItems) {
          return (hospital) => [
            // 医療機関コード
            {
              fieldId: fieldId,
              value: hospital.ownCode, // 藤沢独自コード
            },
            // 医療機関名
            {
              fieldId: fujisawaHospitalItems[fieldId].name,
              value: hospital.hospitalName,
            },
            // 医療機関実施形態
            {
              fieldId: fujisawaHospitalItems[fieldId].embodiment,
              value: stringifyEmbodimentTypeInFujisawa(hospital.hospitalEmbodimentType),
            },
          ];
        }
        return undefined;
    }
  }
};

const stringifyEstablishmentType = (
  establishmentType: components['schemas']['EstablishmentType']
): string => {
  switch (establishmentType) {
    case 'NATIONAL':
      return '001:国立';
    case 'PUBLIC':
      return '002:公立';
    case 'KOUTEKI':
      return '003:公的';
    case 'PRIVATEHOSPITAL':
      return '004:私的病院';
    case 'PRIVATECLINIC':
      return '005:私的診療所';
    default:
      return '';
  }
};

const stringifyEstablishmentTypeAtPatientInfo = (
  establishmentType: components['schemas']['EstablishmentType']
): string => {
  switch (establishmentType) {
    case 'NATIONAL':
      return '001:国立';
    case 'PUBLIC':
      return '002:公立';
    case 'KOUTEKI':
      return '003:公的';
    case 'PRIVATEHOSPITAL':
      return '004:私的';
    case 'PRIVATECLINIC':
      return '004:私的';
    case 'SEKKOTSUIN':
      return '005:その他（医療機関以外）';
    case 'OTHER':
      return '005:その他（医療機関以外）';
    default:
      return '';
  }
};

const stringifyEstablishmentTypeAtPatientInfoOnPrivateField = (
  establishmentType: components['schemas']['EstablishmentType']
): string => {
  switch (establishmentType) {
    case 'PRIVATEHOSPITAL':
      return '001:病院';
    case 'PRIVATECLINIC':
      return '002:診療所';
    default:
      return '';
  }
};

const stringifyEstablishmentTypeAtPatientInfoOnOtherField = (
  establishmentType: components['schemas']['EstablishmentType']
): string => {
  switch (establishmentType) {
    case 'SEKKOTSUIN':
      return '001:接骨院';
    case 'OTHER':
      return '002:その他';
    default:
      return '';
  }
};

const stringifyEmergencyType = (emergencyType: components['schemas']['EmergencyType']): string => {
  switch (emergencyType) {
    case 'EMERGENCY':
      return '001:救急告示';
    case 'OTHER':
      return '002:非告示';
    default:
      return '';
  }
};

const stringifyEmergencyTypeAtPatientInfo = (
  emergencyType: components['schemas']['EmergencyType']
): string => {
  switch (emergencyType) {
    case 'EMERGENCY':
      return '001:救急告示病院';
    case 'OTHER':
      return '002:それ以外';
    default:
      return '';
  }
};

const stringifyEmergencyTypeInFujisawa = (
  emergencyType: components['schemas']['EmergencyType']
): string => {
  switch (emergencyType) {
    case 'EMERGENCY':
      return '1:救急告示';
    default:
      return '';
  }
};

const stringifyEmbodimentType = (
  embodimentType: components['schemas']['EmbodimentType']
): string => {
  switch (embodimentType) {
    case 'INITIAL':
      return '001:初期医療機関';
    case 'SECONDARY':
      return '002:二次医療機関';
    case 'TERTIARY':
      return '003:三次医療機関';
    default:
      return '';
  }
};

const stringifyEmbodimentTypeAtPatientInfo = (
  embodimentType: components['schemas']['EmbodimentType']
): string => {
  switch (embodimentType) {
    case 'INITIAL':
      return '001:初期';
    case 'SECONDARY':
      return '002:二次';
    case 'TERTIARY':
      return '003:三次';
    case 'OTHER':
      return '004:その他';
    default:
      return '';
  }
};

const stringifyEmbodimentTypeInFujisawa = (
  embodimentType: components['schemas']['EmbodimentType']
): string => {
  switch (embodimentType) {
    case 'INITIAL':
      return '初期';
    case 'SECONDARY':
      return '二次';
    case 'TERTIARY':
      return '三次';
    default:
      return '';
  }
};

const stringifyJurisdiction = (jurisdiction: components['schemas']['Jurisdiction']): string => {
  switch (jurisdiction) {
    case 'INSIDE':
      return '001:管内';
    case 'OUTSIDE':
      return '002:管外';
    default:
      return '';
  }
};

const stringifyJurisdictionInFujisawa = (
  jurisdiction: components['schemas']['Jurisdiction']
): string => {
  switch (jurisdiction) {
    case 'INSIDE':
      return '1:管内';
    case 'OUTSIDE':
      return '2:管外';
    default:
      return '';
  }
};

const fujisawaHospitalItems: { [key: string]: { name: string; embodiment: string } } = {
  "cbcf6e3d-5b8e-4014-9c34-ee7a46ca3d79": {
    "name": "4f3152ee-3a42-4981-a54b-6c0963e47bca",
    "embodiment": "34336796-2c79-4f20-bda0-51fd7f20393c"
  },
  "bbd854e6-fb0b-4778-bec3-d0dc7f2634e7": {
    "name": "8d723b5a-8255-437c-8723-68f9b5dda945",
    "embodiment": "fb3cde41-a367-4896-9b62-a2679ef65974"
  },
  "a26826f2-a1da-48d3-bd16-f043690fbfc6": {
    "name": "f0a86e66-e0e3-40cc-9b79-cf12ee6256ea",
    "embodiment": "da244f86-692e-45f3-bf54-028e23799aa2"
  },
  "84da833e-94a4-479d-8332-1d0e85889dfe": {
    "name": "8589f82e-8de2-40bb-b36d-87e720eb297b",
    "embodiment": "08267459-4273-457e-8137-da72e6b2b30d"
  },
  "f81f99ee-ac4e-42c7-b4d4-3c90b34f9f33": {
    "name": "c673f7dd-f3bc-477f-9c20-4d52c47cd0a2",
    "embodiment": "f4a1a67d-0092-4ba9-96b6-f8113f5d531a"
  },
  "cd0254fb-e2d0-4dda-9f2e-992ddc226847": {
    "name": "c8baf3ab-b60f-4f10-85b8-621884d2b29a",
    "embodiment": "fcbeb889-c386-4f5d-9522-388894d5f21b"
  },
  "e03a7470-eda8-4494-9c91-88be2b68cbb5": {
    "name": "e5c8fbff-03a3-40c6-87dc-16e100ab6986",
    "embodiment": "b1f24610-5474-4692-993e-799c6b215ee7"
  },
  "3d43a5b1-1137-430d-9461-3aab2a8b79a8": {
    "name": "98be995f-2afd-4dbf-9f21-cb3e76dcddff",
    "embodiment": "4e08db33-2110-4325-a5f6-d8aaf5a69b92"
  },
  "7b3fea4e-dfc2-4138-83de-e5cae427a462": {
    "name": "d979f633-acd1-40f3-b676-a68dd08e3972",
    "embodiment": "c5aecfc8-aa22-4512-b0b3-1addcc814c68"
  },
  "666c4a7a-58b5-405d-8739-a92fe405c34b": {
    "name": "ee6dea24-b534-4c84-bcc8-0f1a45a31c50",
    "embodiment": "b47c151c-5101-4f93-93e5-7198271733fd"
  },
  "cc348e26-c513-4fa3-b1a0-c1fd1d83b840": {
    "name": "763b6759-f413-4796-b1bc-58cbfef005c7",
    "embodiment": "8b0dc273-2c56-49af-beb0-802f65aa5fa2"
  },
  "6395aa11-ae2d-4556-8b86-d20e5f8f1d06": {
    "name": "512eb527-f7ca-4b18-9668-5064130ac92e",
    "embodiment": "31e4af33-bbf0-4d53-86c4-37dc81aac495"
  },
  "0bd6cc5a-a1e6-4939-9e36-8d53c03f9966": {
    "name": "21d0b3fa-aabf-4d33-9b8e-ea960fa225b7",
    "embodiment": "6c6e68d0-3d31-4269-9090-aa67ce674d3e"
  },
  "de3c4e37-eda4-4f7c-8e08-c6041cfbad1a": {
    "name": "b05138ec-2229-4ce5-ac69-74a8a22ba8c9",
    "embodiment": "b7c9ae78-490d-4555-bdd5-4a04b40f0fcc"
  },
  "637c72e3-8a3e-4f95-a1ed-8d0f45bba9a9": {
    "name": "49d8eeba-5e3d-4d5d-9002-922f307f9a95",
    "embodiment": "16b2c0c0-f583-4061-bc6d-2334629fd542"
  },
  "ee90dd56-70f6-4fb5-8616-1f3d57628a5f": {
    "name": "ee824867-5bd8-4dc7-869f-446b60d39f8f",
    "embodiment": "7ae8e1cb-c88d-4392-b389-41dde9fd9386"
  },
  "b697d34c-9e37-46eb-8085-32a4ed2b7e29": {
    "name": "f144aa18-564a-41e9-bbb0-68c52feb5dd2",
    "embodiment": "4bc80c4f-cb30-4d09-b5df-102a505febf5"
  },
  "89309a0e-027f-47bd-a0d5-777d42404f65": {
    "name": "367882c5-24af-456b-93a0-588b1b133dac",
    "embodiment": "0b1bc77f-28e4-4b5a-964a-4b9919ced9b0"
  },
  "87d1a2d8-a7e9-4fd8-9d98-dce91f273f7f": {
    "name": "549e26b2-1247-4799-8d97-21ecc2a1d44f",
    "embodiment": "b9074ec8-2c65-409c-bd89-497c8178dbdc"
  },
  "e6bccf85-8a0e-4b68-bfc7-ac26ba73ae6c": {
    "name": "a242e218-fab4-48c6-bfed-a8122425df1a",
    "embodiment": "9c6dd9fa-1151-429b-9dd2-141eb0dac06d"
  },
  "635b3ea8-4549-4484-b1f7-948c41829f33": {
    "name": "0e78c989-c226-4719-b417-5befdc9acb5f",
    "embodiment": "a8186251-4b6a-4593-8328-2f7980f78249"
  },
  "752972f0-ee53-45ed-bbea-2f1d5fd70f5f": {
    "name": "643d7b64-54a1-4e4f-bd1f-21e430db141b",
    "embodiment": "74fe7cc9-f8e0-47d7-b49a-9818a84cc2a9"
  },
  "10d19963-b675-49d2-a5e8-0a7fb8f9644d": {
    "name": "82319db4-b154-4709-a97f-b3b187b08ee3",
    "embodiment": "cefb3195-9df3-4782-9c3d-240bb9293f4c"
  },
  "e1dc5dcc-2d97-489c-b054-ab01308a6836": {
    "name": "c0d7a80b-badc-41b4-923a-c82f2e8840bf",
    "embodiment": "130b272c-dd77-4c7f-8428-21183a3bb789"
  },
  "29ddeab3-fb98-421f-b612-eb1434a9d868": {
    "name": "02db9b16-bc28-4a25-8dd7-20346770b0c8",
    "embodiment": "e264b488-e7f6-4c39-a128-c7089994aabc"
  },
  "e0d484d5-550e-4e9d-b1c2-8dfb54d974a2": {
    "name": "a4d47942-da70-4863-ab1b-d1da4e699f35",
    "embodiment": "6b8042fc-2f75-4db0-9a4c-6010bee1e929"
  },
  "b16855fb-8209-47db-95ec-6c6108f6086d": {
    "name": "167d1ed1-3a87-4bcc-86a0-392f89cf5316",
    "embodiment": "2af30ba0-4318-4c9c-b9dd-d362d3b70b6d"
  },
  "c95890a2-d85d-4597-bbeb-cdca86c72577": {
    "name": "441e06e1-0e81-4b30-8328-b71d87d1e718",
    "embodiment": "ff03a58c-f827-466f-8a7f-b4c35b821457"
  },
  "762a6e6d-9832-4892-9a95-2f1b28a1f011": {
    "name": "05253c4f-4b9f-422a-8ea4-458cb5d39921",
    "embodiment": "73fc3b66-f024-4d31-a6bd-09a24cca652f"
  },
  "1a8adee2-0444-4109-bc25-e7e4504bf05e": {
    "name": "8b975fa5-90e1-438f-b1bd-d5133c558d41",
    "embodiment": "1e79687b-eb51-47ee-b647-b68851f37a29"
  },
  "19ac6e37-80c4-4b52-a21e-82b74588d904": {
    "name": "45447775-71f4-4b3a-b0f4-880815793b86",
    "embodiment": "81db530b-4ed8-4861-8de5-934996cbc3b3"
  },
  "9b21d27f-e854-49c7-9416-3e1a8abd83b2": {
    "name": "cfc156d3-5efc-4b31-8567-4e54e6bd82ae",
    "embodiment": "cdbe106d-c226-49ad-8714-e86fbb090a20"
  },
  "a2c1bdfd-45b8-479a-b305-7a8802cabd20": {
    "name": "998744b1-42f2-469e-a36d-0fe50b1fe59f",
    "embodiment": "110850ba-bcf7-469e-a165-de0d9a8f1ddc"
  },
  "b9007480-ea25-436c-b847-a9c9eed92f01": {
    "name": "f9f003bc-a2f7-4138-b09b-43fe341f73c6",
    "embodiment": "58acdea2-98f5-4bb5-b533-02960ad5d316"
  },
  "79680a6b-777d-4ace-ad1e-f469c6f27f63": {
    "name": "1e08e5c0-767b-4be2-b99a-e90999b4ae3b",
    "embodiment": "097bbdb1-3728-46e4-8bf1-9132162425ac"
  },
  "8b6bb10d-98fa-442d-ba17-e31a3111c660": {
    "name": "950a61e4-461d-4273-94bd-9698d56a1fc9",
    "embodiment": "c26eeea1-1fa1-4bc4-a98d-6cc11151ee25"
  },
  "ce9b6d49-4037-4bea-9a74-0a61a9ceb972": {
    "name": "7acb4e27-e69a-4d4f-9be0-6bf7a203be33",
    "embodiment": "96862936-865c-4ce5-a605-7be80842f7bf"
  },
  "f47ceb85-8284-4ca1-b10e-196baf2b167e": {
    "name": "46e3ffb3-d08d-4902-811d-cb73ba25e4c1",
    "embodiment": "124b1bed-1681-4061-8911-d5ac5613e3f6"
  },
  "1e1fa7b1-0745-482d-aa61-55bbb96705f4": {
    "name": "eb10ada6-68a9-4451-b5a1-7fc672c096d2",
    "embodiment": "0b4df28f-27a2-42be-8748-6cacf8cc5015"
  },
  "fe7470bd-bb01-41fe-b06f-3425e931e456": {
    "name": "1f1bd550-b062-483a-b78a-18a489f8a612",
    "embodiment": "ec87e538-2c7f-474a-a31d-4ad87b41ea1c"
  }
}